import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/site/Layout";
import data from "@/data/track-record.json";
import { AuditStandard, AUDIT_METHOD } from "@/components/archive/AuditStandard";
import archiveRows from "@/data/case-study-archive.json";
import { LedgerReplayDialog } from "@/components/archive/LedgerReplayDialog";
import { Play } from "lucide-react";

const CANONICAL = "https://xauproacademy.com/track-record";

export const Route = createFileRoute("/track-record")({
  head: () => {
    const title = "Verified Gold Trading Signals Track Record 2024 to 2026 | XAU Pro Academy";
    const description =
      "Transparent XAUUSD signals track record: 241 closed positions, +95.94R, profit factor 1.72, max drawdown 8.25R. Month by month results built for funded and prop firm accounts.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "keywords",
          content:
            "verified gold trading signals, XAUUSD signals track record, reliable forex signals, prop firm gold signals, funded account trading, pass prop firm challenge, gold signals results, low drawdown trading",
        },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: "Verified Gold Signals Track Record: +95.94R Over 34 Months" },
        {
          property: "og:description",
          content:
            "Every closed XAUUSD position from 2024 to October 2026. Profit factor 1.72, max drawdown 8.25R, inside typical prop firm limits.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: CANONICAL },
        { property: "og:site_name", content: "XAU Pro Academy" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Verified Gold Signals Track Record: +95.94R Over 34 Months" },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: CANONICAL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Dataset",
                name: "XAUUSD Signals Performance Record 2024 to 2026",
                description,
                url: CANONICAL,
                license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", distribution: [
                  { "@type": "DataDownload", encodingFormat: "text/html", contentUrl: CANONICAL },
                  { "@type": "DataDownload", name: "Closed trades", encodingFormat: "text/csv", contentUrl: "https://xauproacademy.com/downloads/xauusd-closed-trades.csv" },
                  { "@type": "DataDownload", name: "Monthly performance", encodingFormat: "text/csv", contentUrl: "https://xauproacademy.com/downloads/xauusd-monthly-performance.csv" },
                ],
                temporalCoverage: "2024-01-01/2026-10-31",
                spatialCoverage: { "@type": "Place", name: "Global spot gold market (XAUUSD)" },
                identifier: "https://xauproacademy.com/track-record#dataset",
                alternateName: "XAU Pro Academy Gold Signals Track Record",
                version: "2026.10",
                dateModified: "2026-11-02",
                citation: "XAU Pro Academy. XAUUSD Signals Performance Record 2024 to 2026. https://xauproacademy.com/track-record",
                creator: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
                publisher: { "@type": "Organization", name: "XAU Pro Academy", url: "https://xauproacademy.com" },
                keywords: ["gold trading signals", "XAUUSD", "track record", "prop firm", "funded account"],
                measurementTechnique: AUDIT_METHOD,
                variableMeasured: [
                  { "@type": "PropertyValue", name: "Net return", unitText: "R", value: data.combined.overall.netR },
                  { "@type": "PropertyValue", name: "Profit factor", value: data.combined.overall.profitFactor },
                  { "@type": "PropertyValue", name: "Maximum drawdown", unitText: "R", value: data.combined.overall.maxDrawdownR },
                  { "@type": "PropertyValue", name: "Win rate", unitText: "percent", value: data.combined.overall.winRate },
                  { "@type": "PropertyValue", name: "Closed positions", value: data.combined.overall.trades },
                ],
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
                  { "@type": "ListItem", position: 2, name: "Performance Record", item: CANONICAL },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: TrackRecordPage,
});

type TabKey = "2024" | "2025" | "2026" | "benchmark";

const TABS: Array<{ key: TabKey; label: string; sub: string }> = [
  { key: "2024", label: "2024", sub: "Full year" },
  { key: "2025", label: "2025", sub: "Full year" },
  { key: "2026", label: "2026", sub: "To 31 Oct" },
  { key: "benchmark", label: "Benchmark", sub: "3 year view" },
];

const usd = (n: number) => `${n < 0 ? "-" : "+"}$${Math.abs(n).toLocaleString("en-US")}`;
const rr = (n: number) => `${n < 0 ? "-" : "+"}${Math.abs(n).toFixed(2)}R`;

function tone(n: number) {
  if (n > 0) return "text-emerald";
  if (n < 0) return "text-destructive";
  return "text-muted-foreground";
}

function MicroLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{children}</div>
  );
}

function Kpi({ label, value, sub, valueClass }: { label: string; value: string; sub?: string; valueClass?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-4">
      <MicroLabel>{label}</MicroLabel>
      <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${valueClass ?? ""}`}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

type Stats = (typeof data)["combined"]["overall"];

function TierCard({ title, note, s }: { title: string; note: string; s: Stats }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-base font-bold">{title}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {s.trades} positions
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <MicroLabel>Net return</MicroLabel>
          <dd className={`mt-1 font-semibold tabular-nums ${tone(s.netR)}`}>{rr(s.netR)}</dd>
          <dd className="text-xs tabular-nums text-muted-foreground">{usd(s.netUsd)}</dd>
        </div>
        <div>
          <MicroLabel>Accuracy</MicroLabel>
          <dd className="mt-1 font-semibold tabular-nums">{s.winRate.toFixed(1)}%</dd>
          <dd className="text-xs tabular-nums text-muted-foreground">
            {s.wins} of {s.trades}
          </dd>
        </div>
        <div>
          <MicroLabel>Profit factor</MicroLabel>
          <dd className="mt-1 font-semibold tabular-nums">{s.profitFactor.toFixed(2)}</dd>
        </div>
        <div>
          <MicroLabel>Max drawdown</MicroLabel>
          <dd className="mt-1 font-semibold tabular-nums">{s.maxDrawdownR.toFixed(2)}R</dd>
        </div>
      </dl>
    </div>
  );
}

function OutcomeBar({ s }: { s: Stats }) {
  const total = Math.max(1, s.trades);
  const segs = [
    { key: "Target achieved", n: s.target, cls: "bg-emerald", note: "Full objective reached" },
    { key: "Partial and risk free", n: s.riskFree, cls: "bg-gold", note: "Half banked, remainder closed at entry" },
    { key: "Risk invalidation", n: s.invalidated, cls: "bg-destructive", note: "Position closed at defined risk" },
  ];
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="font-display text-base font-bold">Resolution distribution</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        How each closed position finished. Nothing is left open or unreported.
      </p>
      <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {segs.map((sg) => (
          <div key={sg.key} className={sg.cls} style={{ width: `${(sg.n / total) * 100}%` }} />
        ))}
      </div>
      <ul className="mt-4 space-y-3">
        {segs.map((sg) => (
          <li key={sg.key} className="flex items-start gap-3">
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${sg.cls}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium">{sg.key}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {sg.n} ({((sg.n / total) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{sg.note}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

type YearData = (typeof data)["years"][number];

function MonthlyChart({ months }: { months: YearData["months"] }) {
  const peak = Math.max(1, ...months.map((m) => Math.abs(m.netR)));
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="font-display text-base font-bold">Month by month</h3>
      <p className="mt-1 text-xs text-muted-foreground">Net result per calendar month, in risk multiples.</p>
      <div className="mt-5 flex items-end gap-1.5 sm:gap-2">
        {months.map((m) => {
          const h = (Math.abs(m.netR) / peak) * 100;
          const up = m.netR >= 0;
          return (
            <div key={m.month} className="group flex min-w-0 flex-1 flex-col items-center">
              <div
                className={`mb-1 font-mono text-[9px] tabular-nums sm:text-[10px] ${tone(m.netR)}`}
                title={`${m.label}: ${rr(m.netR)} over ${m.trades} positions`}
              >
                {m.netR === 0 ? "0" : m.netR.toFixed(1)}
              </div>
              <div className="flex h-24 w-full items-end justify-center">
                <div
                  className={`w-full rounded-sm transition-opacity group-hover:opacity-80 ${up ? "bg-emerald" : "bg-destructive"}`}
                  style={{ height: `${Math.max(h, m.trades ? 3 : 1)}%` }}
                />
              </div>
              <div className="mt-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground sm:text-[10px]">
                {m.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthlyTable({ months }: { months: YearData["months"] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h3 className="font-display text-base font-bold">Monthly detail</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="px-5 py-3 font-normal">Month</th>
              <th className="px-3 py-3 text-right font-normal">Positions</th>
              <th className="px-3 py-3 text-right font-normal">Accuracy</th>
              <th className="px-3 py-3 text-right font-normal">Net R</th>
              <th className="px-3 py-3 text-right font-normal">Net USD</th>
              <th className="px-5 py-3 text-right font-normal">Alpha / Core</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className="border-b border-border/50 last:border-0">
                <td className="px-5 py-3 font-medium">{m.label}</td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{m.trades}</td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                  {m.trades ? `${m.winRate.toFixed(1)}%` : "n/a"}
                </td>
                <td className={`px-3 py-3 text-right font-semibold tabular-nums ${tone(m.netR)}`}>
                  {m.trades ? rr(m.netR) : "n/a"}
                </td>
                <td className={`px-3 py-3 text-right tabular-nums ${tone(m.netR)}`}>
                  {m.trades ? usd(m.netUsd) : "n/a"}
                </td>
                <td className="px-5 py-3 text-right font-mono text-xs tabular-nums text-muted-foreground">
                  {m.alpha} / {m.core}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlyHeatmap() {
  const monthLabels = data.years[0]?.months.map((month) => month.label) ?? [];
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h3 className="font-display text-base font-bold">Monthly map across years</h3>
        <p className="mt-1 text-xs text-muted-foreground">A compact view of positive, negative and inactive months.</p>
      </div>
      <div className="overflow-x-auto p-5">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[72px_repeat(12,minmax(52px,1fr))] gap-1.5">
            <div />
            {monthLabels.map((label) => (
              <div key={label} className="pb-1 text-center font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
            ))}
            {data.years.flatMap((year) => [
              <div key={`${year.year}-label`} className="flex items-center font-display text-sm font-bold">{year.year}</div>,
              ...year.months.map((month) => {
                const inactive = month.trades === 0;
                const cellTone = inactive
                  ? "bg-muted/25 text-muted-foreground"
                  : month.netR > 0
                    ? "bg-emerald/20 text-emerald"
                    : month.netR < 0
                      ? "bg-destructive/20 text-destructive"
                      : "bg-muted text-muted-foreground";
                return (
                  <div
                    key={`${year.year}-${month.month}`}
                    className={`flex h-11 items-center justify-center rounded-sm font-mono text-[10px] font-semibold tabular-nums ${cellTone}`}
                    title={`${year.year} ${month.label}: ${inactive ? "No closed positions" : rr(month.netR)}`}
                  >
                    {inactive ? "n/a" : rr(month.netR)}
                  </div>
                );
              }),
            ])}
          </div>
        </div>
      </div>
    </div>
  );
}

function EquityCurve() {
  const trades = data.years.flatMap((year) => year.trades).sort((a, b) => a.date.localeCompare(b.date));
  const values = [0];
  for (const trade of trades) values.push((values.at(-1) ?? 0) + trade.r);
  const width = 1000;
  const height = 250;
  const min = Math.min(0, ...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const point = (value: number, index: number) => {
    const x = (index / Math.max(1, values.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 24) - 12;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };
  const zeroY = height - ((0 - min) / range) * (height - 24) - 12;
  const yearStarts = data.years.slice(1).map((year) => {
    const index = trades.findIndex((trade) => new Date(trade.date).getUTCFullYear() === year.year);
    return { year: year.year, x: (index / Math.max(1, trades.length - 1)) * width };
  });

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="font-display text-base font-bold">Cumulative return curve</h3>
      <p className="mt-1 text-xs text-muted-foreground">Closed-position sequence from 0R to {rr(values.at(-1) ?? 0)}.</p>
      <div className="mt-5 overflow-hidden rounded-md border border-border bg-background/40 p-3">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label="Cumulative return curve from 2024 through October 2026">
          <line x1="0" y1={zeroY} x2={width} y2={zeroY} className="stroke-border" strokeDasharray="5 5" />
          {yearStarts.map(({ year, x }) => (
            <g key={year}>
              <line x1={x} y1="0" x2={x} y2={height} className="stroke-border" />
              <text x={x + 10} y="20" className="fill-muted-foreground font-mono text-[12px]">{year}</text>
            </g>
          ))}
          <text x="8" y="20" className="fill-muted-foreground font-mono text-[12px]">2024</text>
          <polyline points={values.map(point).join(" ")} fill="none" className="stroke-gold" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}

function RiskUnitScenarios() {
  const units = [200, 500, 1000, 2000];
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h3 className="font-display text-base font-bold">Return at different risk units</h3>
        <p className="mt-1 text-xs text-muted-foreground">A direct scaling comparison. Position volume is not fixed because defined risk depends on the account and invalidation distance.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="px-5 py-3 font-normal">Risk unit</th>
              {data.combined.byYear.map((year) => <th key={year.year} className="px-3 py-3 text-right font-normal">{year.year}</th>)}
              <th className="px-3 py-3 text-right font-normal">Combined</th>
              <th className="px-5 py-3 text-right font-normal">Deepest decline</th>
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <tr key={unit} className="border-b border-border/50 last:border-0">
                <td className="px-5 py-3 font-semibold tabular-nums">${unit.toLocaleString("en-US")}</td>
                {data.combined.byYear.map((year) => (
                  <td key={year.year} className="px-3 py-3 text-right font-semibold tabular-nums text-emerald">{usd(year.netR * unit)}</td>
                ))}
                <td className="px-3 py-3 text-right font-bold tabular-nums text-emerald">{usd(data.combined.overall.netR * unit)}</td>
                <td className="px-5 py-3 text-right font-semibold tabular-nums text-destructive">{usd(-data.combined.overall.maxDrawdownR * unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">Illustrative gross outcomes before trading costs. Scaling the risk unit scales both returns and drawdowns proportionally.</p>
    </div>
  );
}

function StatisticalHighlights() {
  const c = data.combined;
  const s = c.overall;
  const totalMonths = c.positiveMonths + c.negativeMonths + c.flatMonths;
  const averageActivity = s.trades / totalMonths;
  const alphaReturnShare = (c.alpha.netR / s.netR) * 100;
  const alphaActivityShare = (c.alpha.trades / s.trades) * 100;
  const targetShare = (s.target / s.trades) * 100;
  const longR = data.years.flatMap((year) => year.trades).filter((trade) => trade.side === "LONG").reduce((sum, trade) => sum + trade.r, 0);
  const shortR = s.netR - longR;
  const highlights = [
    { value: `${c.positiveMonths} of ${totalMonths}`, label: "Positive months", note: `${c.negativeMonths} negative and ${c.flatMonths} flat across the verified record.` },
    { value: `${s.target}`, label: "Full objectives", note: `${targetShare.toFixed(0)} percent of positions reached their complete objective.` },
    { value: `${s.maxLossStreak}`, label: "Longest loss run", note: "The longest sequence of consecutive risk invalidations." },
    { value: `${averageActivity.toFixed(1)}`, label: "Positions per month", note: "Average activity remained selective across the reporting period." },
    { value: `${alphaReturnShare.toFixed(0)}%`, label: "Alpha return share", note: `Generated from ${alphaActivityShare.toFixed(0)} percent of all closed positions.` },
    { value: `${rr(longR)} / ${rr(shortR)}`, label: "Long / short return", note: "Both market directions contributed to the cumulative result." },
  ];
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4 border-b border-border pb-3">
        <h3 className="font-display text-base font-bold">Statistical highlights</h3>
        <MicroLabel>Three year record</MicroLabel>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {highlights.map((item) => (
          <div key={item.label} className="rounded-lg border border-border bg-card p-5">
            <div className="font-display text-2xl font-bold tabular-nums text-gold">{item.value}</div>
            <div className="mt-2 text-sm font-semibold">{item.label}</div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{item.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

type Trade = YearData["trades"][number];

const OUTCOME_LABEL: Record<Trade["outcome"], string> = {
  TARGET: "Target achieved",
  RISK_FREE: "Partial, risk free",
  INVALIDATED: "Risk invalidation",
};

const ARCHIVE_IDS = new Set(archiveRows.map((a) => a.id));

// Archive positions replay from the verified tape (arc- ids). Live desk
// positions from October 2026 carry the case study id of the engine that
// closed them (otas- or osas-), stored on the row as replayId.
function replayIdFor(t: Trade): string | null {
  const live = (t as { replayId?: string }).replayId;
  if (live) return live;
  return ARCHIVE_IDS.has(t.id) ? `arc-${t.id}` : null;
}

function Ledger({ trades }: { trades: Trade[] }) {
  const [tier, setTier] = useState<"ALL" | "ALPHA" | "CORE">("ALL");
  const [outcome, setOutcome] = useState<"ALL" | Trade["outcome"]>("ALL");
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState<{ ref: string; caseId: string } | null>(null);

  const rows = useMemo(
    () =>
      trades
        .filter((t) => (tier === "ALL" || t.tier === tier) && (outcome === "ALL" || t.outcome === outcome))
        .sort((a, b) => b.date.localeCompare(a.date)),
    [trades, tier, outcome],
  );

  const chip = (active: boolean) =>
    `rounded-md border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
      active
        ? "border-gold bg-gold-muted text-gold"
        : "border-border text-muted-foreground hover:border-gold/40 hover:text-foreground"
    }`;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-5 py-4">
        <h3 className="font-display text-base font-bold">Position ledger</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Every closed position in this period, timestamped in UTC.
          {trades.some((t) => replayIdFor(t)) ? " Select any reference to replay it candle by candle." : ""}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {(["ALL", "ALPHA", "CORE"] as const).map((k) => (
            <button key={k} type="button" className={chip(tier === k)} onClick={() => setTier(k)}>
              {k === "ALL" ? "All tiers" : k === "ALPHA" ? "Tier Alpha" : "Tier Core"}
            </button>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(["ALL", "TARGET", "RISK_FREE", "INVALIDATED"] as const).map((k) => (
            <button key={k} type="button" className={chip(outcome === k)} onClick={() => setOutcome(k)}>
              {k === "ALL" ? "All outcomes" : OUTCOME_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
          No positions match these filters.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="px-5 py-3 font-normal">Reference</th>
                  <th className="px-3 py-3 font-normal">Date (UTC)</th>
                  <th className="px-3 py-3 font-normal">Direction</th>
                  <th className="px-3 py-3 font-normal">Tier</th>
                  <th className="px-3 py-3 font-normal">Outcome</th>
                  <th className="px-5 py-3 text-right font-normal">Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, limit).map((t) => {
                  const caseId = replayIdFor(t);
                  return (
                  <tr
                    key={t.id}
                    onClick={caseId ? () => setOpen({ ref: t.id, caseId }) : undefined}
                    className={`border-b border-border/50 last:border-0 ${caseId ? "cursor-pointer transition-colors hover:bg-gold-muted/40" : ""}`}
                  >
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">
                      {caseId ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpen({ ref: t.id, caseId });
                          }}
                          aria-label={`Replay ${t.id} candle by candle`}
                          className="inline-flex items-center gap-1.5 text-gold underline-offset-4 hover:underline"
                        >
                          <Play className="h-3 w-3" />
                          {t.id}
                        </button>
                      ) : (
                        t.id
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs tabular-nums">
                      {new Date(t.date).toISOString().slice(0, 10)}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.15em] ${
                          t.side === "LONG" ? "text-emerald" : "text-destructive"
                        }`}
                      >
                        {t.side}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${
                          t.tier === "ALPHA" ? "border-gold/40 bg-gold-muted text-gold" : "border-border text-muted-foreground"
                        }`}
                      >
                        {t.tier === "ALPHA" ? "Alpha" : "Core"}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{OUTCOME_LABEL[t.outcome]}</td>
                    <td className={`px-5 py-3 text-right font-semibold tabular-nums ${tone(t.r)}`}>
                      <div>{rr(t.r)}</div>
                      <div className="text-xs font-normal text-muted-foreground">{usd(t.usd)}</div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > limit ? (
            <div className="border-t border-border px-5 py-4 text-center">
              <button
                type="button"
                onClick={() => setLimit((n) => n + 50)}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:underline"
              >
                Show more ({rows.length - limit} remaining)
              </button>
            </div>
          ) : (
            <div className="border-t border-border px-5 py-3 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              {rows.length} positions shown
            </div>
          )}
        </>
      )}
      <LedgerReplayDialog refId={open?.ref ?? null} caseId={open?.caseId ?? null} onClose={() => setOpen(null)} />
    </div>
  );
}

function YearPanel({ y }: { y: YearData }) {
  const s = y.overall;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Net return" value={rr(s.netR)} sub={usd(s.netUsd)} valueClass={tone(s.netR)} />
        <Kpi label="Profit factor" value={s.profitFactor.toFixed(2)} sub="Gross gain over gross loss" />
        <Kpi label="Max drawdown" value={`${s.maxDrawdownR.toFixed(2)}R`} sub="Deepest equity decline" />
        <Kpi label="Accuracy" value={`${s.winRate.toFixed(1)}%`} sub={`${s.wins} of ${s.trades} positions`} />
        <Kpi label="Longest loss run" value={`${s.maxLossStreak}`} sub="Consecutive invalidations" />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <TierCard title="Tier Alpha" note="Highest conviction classification." s={y.alpha} />
        <TierCard title="Tier Core" note="Standard qualified classification." s={y.core} />
        <OutcomeBar s={s} />
      </div>

      <MonthlyChart months={y.months} />
      <MonthlyTable months={y.months} />
      <Ledger trades={y.trades} />
    </div>
  );
}

function BenchmarkPanel() {
  const c = data.combined;
  const s = c.overall;
  const totalMonths = c.positiveMonths + c.negativeMonths + c.flatMonths;
  const worstYearDd = Math.max(...c.byYear.map((y) => y.maxDrawdownR));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Net return" value={rr(s.netR)} sub={usd(s.netUsd)} valueClass={tone(s.netR)} />
        <Kpi label="Profit factor" value={s.profitFactor.toFixed(2)} sub="Across all three years" />
        <Kpi label="Max drawdown" value={`${s.maxDrawdownR.toFixed(2)}R`} sub="Worst decline in 34 months" />
        <Kpi label="Accuracy" value={`${s.winRate.toFixed(1)}%`} sub={`${s.wins} of ${s.trades} positions`} />
        <Kpi label="Positive months" value={`${c.positiveMonths} / ${totalMonths}`} sub={`${c.negativeMonths} negative, ${c.flatMonths} flat`} />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="border-b border-border px-5 py-4">
          <h3 className="font-display text-base font-bold">Year on year</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Side by side comparison of all three verified periods.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-5 py-3 font-normal">Period</th>
                <th className="px-3 py-3 text-right font-normal">Positions</th>
                <th className="px-3 py-3 text-right font-normal">Accuracy</th>
                <th className="px-3 py-3 text-right font-normal">Profit factor</th>
                <th className="px-3 py-3 text-right font-normal">Max DD</th>
                <th className="px-3 py-3 text-right font-normal">Net R</th>
                <th className="px-5 py-3 text-right font-normal">Net USD</th>
              </tr>
            </thead>
            <tbody>
              {c.byYear.map((y) => (
                <tr key={y.year} className="border-b border-border/50">
                  <td className="px-5 py-3 font-medium">{y.year === 2026 ? "2026 to 31 Oct" : y.year}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{y.trades}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{y.winRate.toFixed(1)}%</td>
                  <td className="px-3 py-3 text-right tabular-nums">{y.profitFactor.toFixed(2)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{y.maxDrawdownR.toFixed(2)}R</td>
                  <td className={`px-3 py-3 text-right font-semibold tabular-nums ${tone(y.netR)}`}>{rr(y.netR)}</td>
                  <td className={`px-5 py-3 text-right tabular-nums ${tone(y.netUsd)}`}>{usd(y.netUsd)}</td>
                </tr>
              ))}
              <tr className="bg-muted/40">
                <td className="px-5 py-3 font-display font-bold">Combined</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{s.trades}</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{s.winRate.toFixed(1)}%</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{s.profitFactor.toFixed(2)}</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{s.maxDrawdownR.toFixed(2)}R</td>
                <td className={`px-3 py-3 text-right font-bold tabular-nums ${tone(s.netR)}`}>{rr(s.netR)}</td>
                <td className={`px-5 py-3 text-right font-bold tabular-nums ${tone(s.netUsd)}`}>{usd(s.netUsd)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <TierCard title="Tier Alpha, three years" note="Highest conviction classification." s={c.alpha} />
        <TierCard title="Tier Core, three years" note="Standard qualified classification." s={c.core} />
      </div>

      <EquityCurve />
      <MonthlyHeatmap />
      <RiskUnitScenarios />
      <StatisticalHighlights />

      <div className="rounded-lg border border-border bg-card p-6">
        <MicroLabel>Capital safety</MicroLabel>
        <h3 className="mt-2 font-display text-lg font-bold">Funded account compatibility</h3>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Most funded programmes enforce a 5 percent daily loss limit and a 10 percent total loss limit. At a risk
          unit of 1 percent of account equity per position, the figures below are the relevant comparison.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-border bg-background/40 px-4 py-4">
            <MicroLabel>Deepest decline</MicroLabel>
            <div className="mt-2 font-display text-2xl font-bold tabular-nums">{s.maxDrawdownR.toFixed(2)}%</div>
            <div className="mt-1 text-xs text-muted-foreground">Against a 10 percent total limit</div>
          </div>
          <div className="rounded-md border border-border bg-background/40 px-4 py-4">
            <MicroLabel>Worst single year</MicroLabel>
            <div className="mt-2 font-display text-2xl font-bold tabular-nums">{worstYearDd.toFixed(2)}%</div>
            <div className="mt-1 text-xs text-muted-foreground">No annual period approached the limit</div>
          </div>
          <div className="rounded-md border border-border bg-background/40 px-4 py-4">
            <MicroLabel>Longest loss run</MicroLabel>
            <div className="mt-2 font-display text-2xl font-bold tabular-nums">{s.maxLossStreak}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Equivalent to {s.maxLossStreak} percent of equity at a 1 percent unit
            </div>
          </div>
        </div>
        <p className="mt-5 text-xs text-muted-foreground">
          Position sizing is the account holder decision. Larger risk units scale both the return and the decline
          proportionally.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <MicroLabel>Observations</MicroLabel>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <li>
            Tier Alpha produced {c.alpha.netR.toFixed(2)}R from {c.alpha.trades} positions, which is{" "}
            {((c.alpha.netR / s.netR) * 100).toFixed(0)} percent of total return from{" "}
            {((c.alpha.trades / s.trades) * 100).toFixed(0)} percent of activity.
          </li>
          <li>
            {c.positiveMonths} of {totalMonths} calendar months closed positive, with the weakest month at{" "}
            {c.worstMonthR.toFixed(2)}R and the strongest at {rr(c.bestMonthR)}.
          </li>
          <li>
            Accuracy below 50 percent is normal for this profile. Return comes from the size of resolved objectives
            relative to defined risk, not from frequency of winners.
          </li>
          <li>
            Activity averages roughly {Math.round(s.trades / 34)} positions per month. Selectivity is deliberate.
          </li>
        </ul>
      </div>
    </div>
  );
}

function TrackRecordPage() {
  const [tab, setTab] = useState<TabKey>("2024");
  const year = data.years.find((y) => String(y.year) === tab);
  const s = data.combined.overall;

  return (
    <Layout>
      <section className="border-b border-border bg-slate-deep">
        <div className="container mx-auto max-w-7xl px-4 py-14 sm:py-20">
          <MicroLabel>Orion Decision Engine</MicroLabel>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold leading-tight sm:text-5xl">
            Verified Gold Trading Signals: Performance Record
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Every XAUUSD position the engine resolved across {s.trades} closed outcomes, from January 2024 to 30
            October 2026. Reported in risk multiples for professionals and in dollars for everyone else.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-5">
            <div>
              <MicroLabel>Net return</MicroLabel>
              <div className={`mt-1 font-display text-3xl font-bold tabular-nums ${tone(s.netR)}`}>{rr(s.netR)}</div>
            </div>
            <div>
              <MicroLabel>Profit factor</MicroLabel>
              <div className="mt-1 font-display text-3xl font-bold tabular-nums">{s.profitFactor.toFixed(2)}</div>
            </div>
            <div>
              <MicroLabel>Max drawdown</MicroLabel>
              <div className="mt-1 font-display text-3xl font-bold tabular-nums">{s.maxDrawdownR.toFixed(2)}R</div>
            </div>
            <div>
              <MicroLabel>Positions</MicroLabel>
              <div className="mt-1 font-display text-3xl font-bold tabular-nums">{s.trades}</div>
            </div>
          </div>
        </div>
      </section>

      <AuditStandard />

      <section aria-labelledby="open-data" className="container mx-auto max-w-7xl px-4 py-6">
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Open data</p>
          <h2 id="open-data" className="mt-1 font-display text-lg font-bold">Download the record (CSV)</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Closed outcomes in R units with date, direction and closure type. Selection logic is not part of the dataset.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href="/downloads/xauusd-closed-trades.csv" download className="rounded-md border border-primary/40 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10">Closed trades (.csv)</a>
            <a href="/downloads/xauusd-monthly-performance.csv" download className="rounded-md border border-primary/40 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10">Monthly performance (.csv)</a>
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-7xl px-4 py-10">
        <div className="grid gap-6 lg:grid-cols-[180px_minmax(0,1fr)]">
          <nav aria-label="Reporting period" className="flex min-w-0 gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  aria-current={active ? "page" : undefined}
                  className={`shrink-0 rounded-lg border px-4 py-3 text-left transition-colors ${
                    active
                      ? "border-gold bg-gold-muted"
                      : "border-border bg-card hover:border-gold/40"
                  }`}
                >
                  <div className={`font-display text-base font-bold ${active ? "text-gold" : ""}`}>{t.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    {t.sub}
                  </div>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0">{tab === "benchmark" || !year ? <BenchmarkPanel /> : <YearPanel y={year} />}</div>
        </div>

        <div className="mt-10 rounded-lg border border-border bg-card p-6 lg:ml-[204px]">
          <MicroLabel>Reporting basis</MicroLabel>
          <ul className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <li>
              One risk unit (1R) is the capital placed at risk on a single position. Dollar figures assume a
              {" "}${data.riskPerTradeUsd.toLocaleString("en-US")} risk unit.
            </li>
            <li>
              Results are gross of spread and commission. Typical gold dealing costs reduce net outcome by roughly 8
              to 10 percent.
            </li>
            <li>
              2026 covers 1 January to 31 October 2026. Later periods are added once the calendar month closes.
            </li>
            <li>
              Past performance is historical record, not a forecast. Trading carries risk of capital loss.
            </li>
          </ul>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            Record compiled {new Date(data.generatedAt).toISOString().slice(0, 10)}
          </p>
        </div>
      </section>
      <section className="container mx-auto max-w-4xl px-4 pb-16">
        <h2 className="font-display text-2xl font-bold">How to judge a gold signals provider</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Reliable trading signals are measured by a complete, month by month record, not by selected winning
          screenshots. This page publishes every closed XAUUSD position from January 2024 to 31 October 2026,
          including losing months, so the full distribution of outcomes is visible.
        </p>
        <h3 className="mt-6 font-display text-lg font-bold">Why drawdown matters for funded accounts</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Most prop firm challenges fail on drawdown, not on profit targets. Typical firms enforce a 5 percent daily and
          10 percent total loss limit. Across 34 months the deepest decline in this record was 8.25R, and the longest
          run of consecutive losses was 7 positions. With disciplined position sizing, that profile stays inside common
          funded account limits.
        </p>
        <h3 className="mt-6 font-display text-lg font-bold">Reading the numbers</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          1R is the capital at risk on one position. A profit factor of 1.72 means gross gains were 1.72 times gross
          losses. Dollar figures assume a fixed risk unit and are shown gross of dealing costs.
        </p>
      </section>
    </Layout>
  );
}
